import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, OnDestroy } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators, UntypedFormArray } from '@angular/forms';

import {
  Carrier,
  CarrierFields,
  CarrierMetaDataType,
  CarrierSingleField,
  CarrierTypes,
} from '@app/services/carriers/carrier.model';
import { getCarrierDisplayName } from '../../configs';
import {
  carrierProviderDisplayNameMap,
  CarriersFieldsTypes,
  CarrierTypeNames,
} from '@pdqdev/pdq-shared/dist/CarrierData';
import { ShopService } from '../../../../services/shops/shop.service';
import { ECommerceSubTenant } from '@pdqdev/pdq-shared/dist/entities';
import { cloneDeep } from 'lodash';

@Component({
  selector: 'app-carrier-form',
  templateUrl: './carrier-form.component.html',
  styleUrls: ['./carrier-form.component.scss'],
  standalone: false,
})
export class CarrierFormComponent implements OnInit, OnChanges, OnDestroy {
  @Input() defaultValues: Carrier;
  @Input() carrierFields: CarrierFields;
  @Input() drawer: any;
  @Input() pending: boolean;
  @Input() errorMessage: boolean;
  @Input() formLoaded: boolean = false;
  @Input() carrierType: CarrierTypes;
  @Input() submitButtonName: string;
  @Input() timeZone: string;
  @Input() isOnTrialPlan: boolean = false;
  @Output() submitForm = new EventEmitter<{ carrier: Carrier; metadata?: { reauthenticate?: boolean } }>();
  @Output() cancelCallback = new EventEmitter<Carrier>();
  @Output() onBackCallback = new EventEmitter<void>();
  @Input() operationMode: 'edit' | 'create';

  CarrierFieldConfigType = CarriersFieldsTypes;
  CarrierMetaDataType = CarrierMetaDataType;
  formGroup: UntypedFormGroup;

  subTenantControl;
  carrierConfigDataLoaded = false;
  formSubmitAttempt = false;
  carrierDisplayName: string;
  currentFormArrayName: UntypedFormArray;
  arrowFunctionArray: any[] = [];
  isArrowOpen: boolean = false;
  eCommerceSubTenants: ECommerceSubTenant[] = [];
  isMultiMerchantShop: boolean = false;
  isTestModeSupported: boolean = false;
  toolTipLabelOnTestMode: string;

  carrierOperationMode: 'TEST' | 'LIVE';

  readonly ERROR_REQUIRED_MESSAGE = 'This field is required';

  constructor(
    private formBuilder: UntypedFormBuilder,
    private shopService: ShopService,
  ) {}

  ngOnInit(): void {
    this.eCommerceSubTenants = this.shopService.shopSubTenants;
    this.isMultiMerchantShop = this.shopService.isMultiMerchant;
    this.carrierDisplayName = getCarrierDisplayName(this.carrierType.name);
    this.isTestModeSupported = this.checkIsTestModeSupported();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.defaultValues || changes.carrierFields) {
      this.initForm();
      this.setCarrierValuesToForm();
      this.isTestModeSupported = this.checkIsTestModeSupported();
      if (this.carrierFields) {
        this.carrierConfigDataLoaded = true;
        if (this.carrierFields.providersFields) {
          for (let i = 0; i < this.carrierFields.providersFields.length; i++) {
            this.arrowFunctionArray[i] = false;
          }
        }
      }
      // this.changeDetectorRef.detectChanges();
      this.updateCarrierOperationMode();
    }
  }

  ngOnDestroy(): void {
    this.formGroup.reset();
  }

  onArrowClick(event, index: number) {
    this.arrowFunctionArray[index] = !this.arrowFunctionArray[index];
  }
  subTenantsSelectHandler(modifiedOption) {
    const eCommerceSubTenantIds = this.formGroup.value.eCommerceSubTenantIds ?? [];
    const eCommerceSubTenantControl = this.formGroup.controls.eCommerceSubTenantIds;
    const existingIdIdx = eCommerceSubTenantIds.findIndex((subTenantId) => subTenantId === modifiedOption.value);
    const existingId = existingIdIdx !== -1;
    let madeChanges = false;
    if (modifiedOption.checked && !existingId) {
      madeChanges = true;
      eCommerceSubTenantIds.push(modifiedOption.value);
    } else if (!modifiedOption.checked && existingId) {
      madeChanges = true;
      eCommerceSubTenantIds.splice(existingIdIdx, 1);
    }
    eCommerceSubTenantControl.patchValue(eCommerceSubTenantIds);
    if (madeChanges) {
      eCommerceSubTenantControl.markAsDirty();
    }
  }
  onSubmit(payload?: { reauthenticate?: boolean }): void {
    this.formSubmitAttempt = true;
    const dataToSubmit = this.getDataForSubmit();

    if (this.formGroup.valid && !this.pending && dataToSubmit) {
      this.submitForm.emit({ carrier: dataToSubmit, metadata: payload });
    }
  }

  private initForm(): void {
    this.formGroup = new UntypedFormGroup(this.getControlsObject());
    // we must init checkbox form array after formGroup is created with metadata values from carrier metadata
    if (!this.defaultValues) {
      this.initFormArray();
    }
  }

  private initFormArray() {
    this.carrierFields?.metaDataFields?.forEach?.((field) => {
      if (field.type === CarriersFieldsTypes.CHECKBOX) {
        field.options.forEach((option) => {
          const checkboxFormArray = this.formGroup.get(field.fieldName) as UntypedFormArray;
          checkboxFormArray.push(this.initCheckboxFormGroup(option));
        });
      }
    });

    if (this.carrierFields?.providersFields && this.providersFormArray) {
      const providersFormGroupData = this.initProvidersFormGroup(this.carrierFields?.providersFields);
      this.providersFormArray.controls = providersFormGroupData;
    }
  }

  get providersFormArray(): UntypedFormArray {
    return this.formGroup.get('providers') as UntypedFormArray;
  }

  private getControlsObject() {
    const controls: any = {};
    const isTestMode = this.defaultValues?.isTestMode;

    controls['isTestMode'] = new UntypedFormControl(this.defaultValues?.isTestMode);

    this.carrierFields?.defaultFields?.forEach?.((field) => {
      const validatorsArray = this.generateValidatorsArray(field, isTestMode);
      controls[field.fieldName] = new UntypedFormControl(field.default, validatorsArray);
    });
    this.carrierFields?.metaDataFields?.forEach?.((field) => {
      if (field.type === CarriersFieldsTypes.CHECKBOX) {
        controls[field.fieldName] = new UntypedFormArray([]);
      } else {
        const validatorsArray = this.generateValidatorsArray(field, isTestMode);
        controls[field.fieldName] = new UntypedFormControl(field.default, validatorsArray);
      }
    });
    if (this.isMultiMerchantShop) {
      controls[`eCommerceSubTenantIds`] = new UntypedFormControl(
        this.defaultValues?.eCommerceSubTenants?.map((subTenant) => subTenant.id) ?? [],
      );
    }
    if (this.carrierFields?.providersFields) {
      controls['providers'] = new UntypedFormArray([]);
    }

    return controls;
  }

  initCheckboxFormGroup(option?: any): UntypedFormGroup {
    return this.formBuilder.group({
      value: new UntypedFormControl(option.value),
      name: new UntypedFormControl(option.name),
      checked: new UntypedFormControl(option.checked),
    });
  }

  initProvidersFormGroup(providers): any {
    const providerFormArrayBuild: any = [];
    let providersConfig: any = {};
    providers.forEach((field) => {
      providersConfig = {
        name: field.name ?? field.displayName ?? field.fieldName,
        enabled: field?.enabled ?? field.defaultValue,
        providerServices: this.formBuilder.array(this.initProvidersServiceLevelFormGroup(field.providerServices)),
      };

      if (field.id) {
        providersConfig.id = field.id;
      }

      providerFormArrayBuild.push(this.formBuilder.group(providersConfig));
    });
    return providerFormArrayBuild;
  }

  onProviderEnableChange(event, providerConfigRef: any) {
    providerConfigRef.enabled = event;
  }

  onProviderServicesEnableChange(event, providerServicesConfigRef: any) {
    providerServicesConfigRef.enabled = event;
  }

  initProvidersServiceLevelFormGroup(providerServices): any {
    const providerServicesArrayBuild: any = [];
    let providerServicesConfig: any = {};

    providerServices.forEach((field) => {
      providerServicesConfig = {
        name: field.name ?? field.fieldName,
        displayName: field.displayName ?? field.displayName,
        enabled: field?.enabled ?? field.defaultValue,
      };

      if (field.id) {
        providerServicesConfig.id = field.id;
      }

      providerServicesArrayBuild.push(this.formBuilder.group(providerServicesConfig));
    });
    return providerServicesArrayBuild;
  }

  private generateValidatorsArray(fieldConfiguration: CarrierSingleField, isTestMode: boolean) {
    const validatorsArray = [];
    if (fieldConfiguration.isRequired && !(isTestMode && fieldConfiguration.isNotRequiredOnTestMode)) {
      validatorsArray.push(Validators.required);
    }

    if (fieldConfiguration.validate?.pattern) {
      validatorsArray.push(Validators.pattern(fieldConfiguration.validate.pattern));
    }

    return validatorsArray;
  }

  private setCarrierValuesToForm(): void {
    if (!this.defaultValues) {
      return;
    }

    const newFieldsValue = {};

    this.carrierFields?.defaultFields?.forEach?.((field) => {
      newFieldsValue[field.fieldName] = this.defaultValues[field.fieldName];
    });
    this.carrierFields?.metaDataFields?.forEach?.((field) => {
      const receivedData = this.getMetaDataValue(field);

      if (field.type === CarriersFieldsTypes.CHECKBOX) {
        const checkboxMetadataField = this.defaultValues?.metadata.find((val) => val.fieldName === field.fieldName);

        this.currentFormArrayName = new UntypedFormArray([]);

        if (!checkboxMetadataField) {
          field.options.forEach((option) => {
            this.currentFormArrayName = this.formGroup.get(field.fieldName) as UntypedFormArray;
            this.currentFormArrayName.push(this.initCheckboxFormGroup(option));
          });
        } else {
          if (checkboxMetadataField) {
            checkboxMetadataField?.options?.forEach((option) => {
              this.currentFormArrayName = this.formGroup.get(field.fieldName) as UntypedFormArray;
              this.currentFormArrayName.push(this.initCheckboxFormGroup(option));
            });
          }
        }
      } else if (field.type === CarriersFieldsTypes.RADIO_BUTTONS) {
        if (field.fieldName === 'labelFormat') {
          field.default = this.defaultValues?.labelConfiguration?.labelFormat;
          newFieldsValue[field.fieldName] = field.default;
        } else {
          const radioMetadataField = this.defaultValues?.metadata?.find((val) => val.fieldName === field.fieldName);
          field.default = radioMetadataField?.default;
          newFieldsValue[field.fieldName] = radioMetadataField?.default;
        }
      } else {
        newFieldsValue[field.fieldName] = receivedData;
      }
    });
    if (this.defaultValues.providers && this.providersFormArray) {
      const providersFormGroupData = this.initProvidersFormGroup(this.defaultValues.providers);
      this.providersFormArray.controls = providersFormGroupData;
    }

    this.formGroup.patchValue({ ...this.formGroup.value, ...newFieldsValue });
  }

  private getMetaDataValue(field: CarrierSingleField) {
    const result =
      this.defaultValues?.metadata?.find?.((metaField) => metaField.fieldName === field.fieldName)?.default ||
      field?.default ||
      null;
    return result;
  }

  private getDataForSubmit(): Carrier {
    const formValue = { ...this.formGroup.value };
    const metaData = this.carrierFields?.metaDataFields ? cloneDeep(this.carrierFields.metaDataFields) : [];

    this.carrierFields?.metaDataFields?.forEach?.((field, index) => {
      if (field.type === CarriersFieldsTypes.CHECKBOX) {
        const defaultCheckboxValues: string[] = [];
        formValue[field.fieldName].map((option) => {
          if (option.checked) {
            defaultCheckboxValues.push(option.value);
          }
          metaData[index].options = formValue[field.fieldName];
          metaData[index].default = defaultCheckboxValues;
        });
      } else if (field.fieldName === 'labelFormat') {
        formValue.labelConfiguration = { labelFormat: formValue.labelFormat };
      } else {
        metaData[index].default = formValue[field.fieldName];
      }
      delete formValue[field.fieldName];
    });

    formValue.type = this.defaultValues?.type ? this.defaultValues.type : this.carrierType;
    formValue.metadata = metaData?.length > 0 ? metaData : null;
    if (this.isMultiMerchantShop) {
      formValue.eCommerceSubTenants = formValue.eCommerceSubTenantIds
        .map((id) => this.eCommerceSubTenants.find((subTenant) => subTenant.id === id))
        .filter(Boolean);
    }
    return formValue;
  }

  getProviderDisplayNameMap(provider: string) {
    return carrierProviderDisplayNameMap[provider];
  }

  checkIsMasterCarrier(): boolean {
    return this.defaultValues?.isMasterCarrierAccount;
  }

  checkIsTestModeSupported(): boolean {
    const isMasterCarrierAccount =
      this.checkIsMasterCarrier() &&
      !this.defaultValues?.isLinkedCarrierAccount &&
      this.carrierType.name !== CarrierTypeNames.DELIVERY_SOLUTIONS_MA;

    this.toolTipLabelOnTestMode = isMasterCarrierAccount
      ? 'Activate billing plan in order to enable production mode'
      : '';
    return (
      isMasterCarrierAccount ||
      this.carrierType.custom_fields?.trackStarIntegration ||
      this.carrierType.custom_fields?.isTestModeSupported
    );
  }

  checkIsHiddenField(fieldConfig: CarrierSingleField): boolean {
    return fieldConfig?.isHidden;
  }

  isDefaultFieldReadonly(fieldConfig: CarrierSingleField): boolean {
    return this.checkIsMasterCarrier() || (this.operationMode === 'edit' && fieldConfig?.isNotEditable);
  }

  isMetaFieldReadonly(fieldConfig: CarrierSingleField): boolean {
    return fieldConfig?.isReadonly || (this.operationMode === 'edit' && fieldConfig?.isNotEditable);
  }
  isEnableOnlyInEditMode(fieldConfig: CarrierSingleField): boolean {
    return !(fieldConfig?.enableOnlyInEditMode && this.operationMode == 'create');
  }

  onIsTestModeToggle() {
    const newValue = !this.formGroup?.controls?.isTestMode?.value;
    this.formGroup?.controls?.isTestMode?.setValue(+newValue);
    this.updateCarrierOperationMode();
  }

  updateCarrierOperationMode() {
    this.carrierOperationMode = this.formGroup?.controls?.isTestMode?.value ? 'TEST' : 'LIVE';
  }

  getFormattedCarrierName(providerConfig) {
    return providerConfig?.name?.toLowerCase()?.replace(' ', '');
  }

  isFieldEnabled(depndentFieldName: string, dependentFieldValue?: string) {
    const dependentControl = this.formGroup.get(depndentFieldName);
    if (!dependentControl) return false;
    if (dependentFieldValue) {
      return dependentControl.value === dependentFieldValue;
    }
    return !!dependentControl.value;
  }
}
